import { useCallback } from "react";
import { toast } from "react-toastify";

import { apiClient } from "../../api/httpClient";
import { buildCantFindBondEmailPayload } from "../Law Firm User/helpers/lawFirmBondsHelpers";
import { getDeleteBondEndpoint } from "../../globals/utils/bondDeleteHelpers";

/** Shared cancel / delete / cant-find submit handlers for law-firm bonds and archives. */
export default function useBondCancelDeleteHandlers({
  selectedBondToCancel,
  setCancelDialogOpen,
  cancellationFile,
  setIsCancelling,
  missingCancellationFileMessage,
  selectedBondToDelete,
  setDeleteDialogOpen,
  setSelectedBondToDelete,
  setIsDeleting,
  refreshData,
  cantFind,
}) {
  const handleCancelBondSubmit = async () => {
    if (!selectedBondToCancel) return;
    if (!cancellationFile) {
      if (missingCancellationFileMessage) {
        toast.error(missingCancellationFileMessage);
      }
      return;
    }
    setIsCancelling(true);
    const formData = new FormData();
    formData.append("cancellation_file", cancellationFile);
    try {
      await apiClient(
        `/cancel-bond/`,
        "POST",
        formData,
        { bond_id: selectedBondToCancel.bond_id },
        { "Content-Type": "multipart/form-data" }
      );
      toast.success("Bond cancelled successfully!");
      setCancelDialogOpen(false);
      await refreshData();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to cancel the bond.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setSelectedBondToDelete(null);
  };

  const handleReviewBond = () => {
    if (!selectedBondToDelete) return;
    const type = selectedBondToDelete.bond_type.toLowerCase().replace(" ", "-");
    window.open(`/vermost-${type}-bond-update/${selectedBondToDelete.bond_id}`, "_blank");
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBondToDelete) return;
    setIsDeleting(true);
    try {
      const endpoint = getDeleteBondEndpoint(
        selectedBondToDelete.bond_type,
        selectedBondToDelete.bond_id
      );
      if (!endpoint) throw new Error("Invalid bond type");
      await apiClient(endpoint, "DELETE");
      toast.success("Bond deleted successfully");
      handleDeleteClose();
      await refreshData();
    } catch (error) {
      console.error("Error deleting bond:", error);
      toast.error("Failed to delete bond");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCantFindBondSubmit = useCallback(async () => {
    if (!cantFind) return;
    const {
      userdetails,
      filters,
      lawFirms,
      lawfirmId,
      cfBondNumber,
      cfBondType,
      cfName,
      cfPrincipalName,
      cfAction,
      cfOtherInfo,
      setCfSubmitting,
      setCantFindDialogOpen,
      setCfBondNumber,
      setCfBondType,
      setCfName,
      setCfPrincipalName,
      setCfAction,
      setCfOtherInfo,
    } = cantFind;

    try {
      setCfSubmitting(true);
      const requesterName =
        `${userdetails?.first_name || ""} ${userdetails?.last_name || ""}`.trim();
      const selectedFirmFromFilters = filters?.lawFirm?.[0]?.label || null;
      const selectedFirmFromList = lawFirms.find((f) => f.value === lawfirmId)?.label;
      const lawFirmName = selectedFirmFromFilters || selectedFirmFromList || "";
      const payload = buildCantFindBondEmailPayload({
        lawFirmName,
        requesterName,
        cfBondNumber,
        cfBondType,
        cfName,
        cfPrincipalName,
        cfAction,
        cfOtherInfo,
      });
      await apiClient(
        `/send-portal-email/`,
        "POST",
        payload,
        {},
        { "Content-Type": "application/json" }
      );
      toast.success("Request submitted. Our team will review and get back to you.");
      setCantFindDialogOpen(false);
      setCfBondNumber("");
      setCfBondType(null);
      setCfName("");
      setCfPrincipalName("");
      setCfAction("cancellation");
      setCfOtherInfo("");
    } catch (err) {
      console.error("Email send failed:", err);
      toast.error("Failed to submit the request. Please try again.");
    } finally {
      setCfSubmitting(false);
    }
  }, [cantFind]);

  return {
    handleCancelBondSubmit,
    handleDeleteClose,
    handleReviewBond,
    handleDeleteConfirm,
    handleCantFindBondSubmit,
  };
}
